import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import type { ProductVariant } from "../types";
import { capFirst } from "../utils/groupVariants";

const apiUrl = process.env.REACT_APP_API_URL;

const UploadProductImage = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [products, setProducts] = useState<ProductVariant[]>([]);
  const [selectedName, setSelectedName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(
    null
  );

  useEffect(() => {
    fetch(`${apiUrl}/api/v1/products`)
      .then((r) => r.json())
      .then((data: ProductVariant[]) => setProducts(data))
      .catch(() => {});
  }, []);

  const namesWithImage = new Set(
    products.filter((p) => !!p.image).map((p) => p.name)
  );
  const uniqueNames = Array.from(new Set(products.map((p) => p.name)))
    .filter((name) => !namesWithImage.has(name))
    .sort();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    if (f) {
      setPreview(URL.createObjectURL(f));
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedName) return;
    setUploading(true);
    setResult(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const form = new FormData();
      form.append("name", selectedName);
      form.append("file", file);

      const res = await fetch(`${apiUrl}/api/v1/products/upload-image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: form,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setResult({
          ok: false,
          message: err.detail ?? "Failed to update product.",
        });
      } else {
        setResult({
          ok: true,
          message: `Image updated for "${capFirst(selectedName)}" (all sizes).`,
        });
        setFile(null);
        setPreview(null);
        setSelectedName("");
        if (inputRef.current) inputRef.current.value = "";
      }
    } catch {
      setResult({ ok: false, message: "Could not reach the server." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 3,
        }}
      >
        Upload Product Image
      </Typography>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 480 }}
      >
        <Autocomplete
          options={uniqueNames}
          getOptionLabel={(name) => capFirst(name)}
          value={selectedName || null}
          onChange={(_, v) => {
            setSelectedName(v ?? "");
            setResult(null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search product"
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
            />
          )}
          noOptionsText="No products found"
        />

        <Box
          sx={{
            border: "1px dashed #d4d4d4",
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": { borderColor: "black" },
          }}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          {preview ? (
            <Box
              component="img"
              src={preview}
              alt="preview"
              sx={{
                maxHeight: 200,
                maxWidth: "100%",
                objectFit: "contain",
                mb: 1,
              }}
            />
          ) : (
            <>
              <Typography variant="body2" sx={{ color: "#737373", mb: 0.5 }}>
                Click to select an image
              </Typography>
              <Typography variant="caption" sx={{ color: "#a3a3a3" }}>
                jpg · png · webp
              </Typography>
            </>
          )}
          {file && (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#737373", mt: 1 }}
            >
              {file.name}
            </Typography>
          )}
        </Box>

        {result && (
          <Alert
            severity={result.ok ? "success" : "error"}
            sx={{ borderRadius: 0 }}
          >
            {result.message}
          </Alert>
        )}

        <Button
          variant="contained"
          disabled={uploading || !file || !selectedName}
          onClick={handleUpload}
          sx={{
            bgcolor: "black",
            color: "white",
            px: 4,
            py: 1.5,
            fontSize: "0.75rem",
            letterSpacing: "0.1em",
            borderRadius: 0,
            alignSelf: "flex-start",
            "&:hover": { bgcolor: "#333" },
          }}
        >
          {uploading ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            "Upload Image"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default UploadProductImage;
